import * as yup from 'yup';

import { parseCurrencyIntoFloat } from 'app/utils/strings';
import { validateDate } from 'app/utils/validations';
import { calculateMinimumPeriodStartDate, isDateBefore } from 'app/utils/date';

export const createPayCalendarProfileFormSchema = (
  payCalendar?: {
    globalAllowancePercentage?: number;
    odpAllowancePercentage?: number;
  },
  companyConfiguration?: {
    modulesConfig?: {
      modules?: {
        ON_DEMAND_PAY?: string;
        RESERVE?: string;
      };
    };
  }
) =>
  yup
    .object()
    .shape({
      name: yup.string().required('Pay calendar name is mandatory'),
      externalPayCalendarId: yup.string(),
      payFrequency: yup.string().required('Pay frequency is mandatory'),
      payType: yup.string().required('Pay type is mandatory'),
      paydayFixedDay: yup.string().when('payday', {
        is: 'FIXED_DAY',
        then: (schema) =>
          schema
            .required('You must provide a valid date in the form dd/mm/yyyy')
            .test('Invalid date', 'Enter a valid date', (value) => !value || validateDate(value))
            // eslint-disable-next-line func-names
            .test('start-date-within-limit', function (value) {
              const { startDate } = this.parent;
              if (!value || !startDate) return true; // if missing, let required or other tests handle it

              // If start date is before the calculated limit date, return an error.
              if (isDateBefore(value, startDate)) {
                const warning = `Your start date is too early, please set to ${startDate} as a minimum`;

                return this.createError({
                  message: warning,
                });
              }

              return true;
            }),
      }),
      startDate: yup
        .string()
        // eslint-disable-next-line func-names
        .test('start-date-within-limit', function (value) {
          // Skip validation for existing pay calendars (editing mode)
          const { id } = this.parent;
          if (id) return true;

          // Access sibling field hasEmployeeWithSalary -- only run the test if there is no employee
          const { hasEmployeeWithSalary } = this.parent;
          if (!hasEmployeeWithSalary) {
            // Access sibling field: payFrequency
            const { payFrequency } = this.parent;
            if (!value || !payFrequency) return true; // if missing, let required or other tests handle it

            let startDateIsBeforePeriodStart = false;
            let startDateIsBeforePeriodStartWarning = '';

            const calculatedMinimumPeriodStart = calculateMinimumPeriodStartDate(payFrequency);
            startDateIsBeforePeriodStart = isDateBefore(value, calculatedMinimumPeriodStart);
            startDateIsBeforePeriodStartWarning = `Your start date is too early, please set to ${calculatedMinimumPeriodStart} as a minimum`;

            // If start date is before the calculated limit date, return an error.
            if (startDateIsBeforePeriodStart) {
              return this.createError({
                message: startDateIsBeforePeriodStartWarning,
              });
            }

            return true;
          }
          return true;
        }),
      payday: yup
        .string()
        .when('payFrequency', (payFrequency, schema) => {
          if (payFrequency.toString() === 'MONTHLY') {
            return schema.oneOf(
              [
                'LAST_DAY_OF_PERIOD',
                'LAST_BUSINESS_DAY',
                'LAST_CUSTOM_WEEKDAY',
                'N_BUSINESS_DAY_OF_PERIOD',
                'N_DAY_OF_PERIOD',
                'FIXED_DAY',
                'CUSTOM_DAY', // backwards compatibility
                'CUSTOM_DAY_IN_ARREARS', // backwards compatibility
                'FIRST_BUSINESS_DAY', // backwards compatibility
                'FIRST_CUSTOM_WEEKDAY', // backwards compatibility
              ],
              'Select a valid option for monthly pay frequency'
            );
          }
          if (['FOUR_WEEKLY', 'FORTNIGHTLY', 'WEEKLY'].includes(payFrequency.toString())) {
            return schema.oneOf(
              ['LAST_DAY_OF_PERIOD', 'LAST_BUSINESS_DAY', 'N_DAY_OF_PERIOD'],
              'Select a valid option for weekly, fortnightly, or 4-weekly pay frequency'
            );
          }
          return schema;
        })
        .required('Payday is mandatory'),
      paydayCustomDay: yup
        .number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .when('payday', (payday, schema) => {
          if (payday.toString() === 'N_BUSINESS_DAY_OF_PERIOD') {
            return schema
              .positive('Please provide a positive number value from 1 to 23')
              .required('Please provide a value from 1 to 23')
              .integer('Please only use whole number values from 1 to 23')
              .min(1, 'Please provide a value from 1 to 23')
              .max(23, 'Please provide a value from 1 to 23');
          }
          if (payday.toString() === 'N_DAY_OF_PERIOD') {
            return schema
              .positive('Please provide a positive number value from 1 to 31')
              .required('Please provide a value from 1 to 31')
              .integer('Please only use whole number values from 1 to 31')
              .min(1, 'Please provide a value from 1 to 31')
              .max(31, 'Please provide a value from 1 to 31');
          }
          return schema;
        }),
      paydayFirstCustomWeekday: yup.string().when('payday', {
        is: 'FIRST_CUSTOM_WEEKDAY',
        then: (schema) => schema.required('First custom weekday is required'),
      }),
      accrualPeriod: yup.string().required('Accrual period is required'),
      salariedDeductionsRequestMadeAvailableConfig: yup
        .string()
        .required('Salaried deductions config is required'),
      nonSalariedDeductionsRequestMadeAvailableConfig: yup
        .string()
        .required('Non-salaried deductions config is required'),
      salariedGpeEnabled: yup
        .boolean()
        .test(
          'salaried-gpe-requires-payday-mat',
          'Get Paid Early can only be enabled when the salaried Made Available Time is set to On Payday.',
          (value, ctx) =>
            !value ||
            ctx.parent.salariedDeductionsRequestMadeAvailableConfig ===
              'DEDUCTIONS_PAY_CYCLE_PAYDAY'
        ),
      salariedGpeDaysBeforePaydayCap: yup
        .number()
        .transform((value, original) =>
          original === '' || original === null || original === undefined ? undefined : value
        )
        .notRequired()
        .integer('Days before payday must be a whole number.')
        .min(1, 'Days before payday must be at least 1.'),
      nonSalariedGpeEnabled: yup
        .boolean()
        .test(
          'non-salaried-gpe-requires-payday-mat',
          'Get Paid Early can only be enabled when the non-salaried Made Available Time is set to On Payday.',
          (value, ctx) =>
            !value ||
            ctx.parent.nonSalariedDeductionsRequestMadeAvailableConfig ===
              'DEDUCTIONS_PAY_CYCLE_PAYDAY'
        ),
      nonSalariedGpeDaysBeforePaydayCap: yup
        .number()
        .transform((value, original) =>
          original === '' || original === null || original === undefined ? undefined : value
        )
        .notRequired()
        .integer('Days before payday must be a whole number.')
        .min(1, 'Days before payday must be at least 1.'),
      paydayNonBusinessDayFailover: yup
        .string()
        .required('You need to choose a default payday action'),
      location: yup.string().required('Location is mandatory'),
      defaultAnnualGrossSalary: yup
        .number()
        .transform((_, originalValue) => {
          const parsedSalary = parseCurrencyIntoFloat(originalValue);
          return Number.isNaN(parsedSalary) ? undefined : parsedSalary;
        })
        .min(0, 'Salary must be a positive number')
        .max(500000, 'The salary must not exceed £500,000'), // Upper bound check

      payrollCutoff: yup
        .string()
        .when('payFrequency', (payFrequency, schema) => {
          if (payFrequency.toString() === 'MONTHLY') {
            return schema.oneOf(
              [
                'N_DAYS_BEFORE_PAYDAY',
                'N_BUSINESS_DAYS_BEFORE_PAYDAY',
                'LAST_CUSTOM_WEEKDAY',
                'N_BUSINESS_DAY_OF_PERIOD',
                'N_DAY_OF_PERIOD',
                'FIXED_DAY',
                'CUSTOM_DAY', // backwards compatibiliity
                'CUSTOM_DAY_IN_ARREARS', // backwards compatibiliity
              ],
              'Select a valid option for monthly pay frequency'
            );
          }
          if (['FOUR_WEEKLY', 'FORTNIGHTLY', 'WEEKLY'].includes(payFrequency.toString())) {
            return schema.oneOf(
              ['N_DAYS_BEFORE_PAYDAY', 'N_BUSINESS_DAYS_BEFORE_PAYDAY', 'N_DAY_OF_PERIOD'],
              'Select a valid option for weekly, fortnightly, or 4-weekly pay frequency'
            );
          }
          return schema;
        })
        .required('Payroll Cutoff day is mandatory'),
      payrollCutoffFixedDay: yup.string().when('payrollCutoff', {
        is: 'FIXED_DAY',
        then: (schema) =>
          schema
            .required('You must provide a valid date in the form dd/mm/yyyy')
            .test('Invalid date', 'Enter a valid date', (value) => !value || validateDate(value)),
      }),

      payrollCutoffCustomDay: yup.string().when('payrollCutOff', (payrollCutoff, schema) => {
        if (
          payrollCutoff.toString() === 'CUSTOM_DAY' ||
          payrollCutoff.toString() === 'CUSTOM_DAY_IN_ARREARS'
        ) {
          return schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(0, 'Please use a value from 0 to 31')
            .max(31, 'Please use a value from 0 to 31')
            .required('Please provide a value from 0 to 31');
        }
        return schema.notRequired();
      }),

      payrollCutoffDays: yup.number().when('payrollCutoff', (payrollCutoff, schema) => {
        if (
          payrollCutoff.toString() === 'FIXED_DAY' ||
          payrollCutoff.toString() === 'CUSTOM_DAY' ||
          payrollCutoff.toString() === 'CUSTOM_DAY_IN_ARREARS'
        ) {
          // Disable validation when using FIXED_DAY
          return schema.notRequired();
        }
        if (payrollCutoff.toString() === 'N_DAYS_BEFORE_PAYDAY') {
          return schema
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .min(0, 'Please use a value from 0 to 31')
            .max(31, 'Please use a value from 0 to 31')
            .required('Please provide a value from 0 to 31')
            .integer('Please only use whole number values from 0 to 31');
        }
        // For all other options, require a value between 1 and 31
        return schema
          .transform((value) => (Number.isNaN(value) ? undefined : value))
          .min(1, 'Please use a value from 1 to 31')
          .max(31, 'Please use a value from 1 to 31')
          .required('Please provide a value from 1 to 31')
          .integer('Please only use whole number values from 1 to 31');
      }),

      cutoffNonBusinessDayFailover: yup
        .string()
        .required('You need to choose a default cut-off action'),
      odpAllowancePercentage: yup.number().when([], {
        is: () => companyConfiguration?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED',
        then: (schema) => schema.required('On-demand pay sub-limit is mandatory'),
        otherwise: (schema) => schema.notRequired(),
      }),
      startingBalanceDays: yup.number().required('1st of month accrual is mandatory'),
      customOdpAllowancePercentage: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('odpAllowancePercentage', {
          is: 200, // When odpAllowancePercentage is 200
          then: (schema) => {
            // Only require custom odp allowance percentage if ON_DEMAND_PAY is enabled
            if (companyConfiguration?.modulesConfig?.modules?.ON_DEMAND_PAY === 'ENABLED') {
              return schema
                .required('Value is required when choosing a Custom %')
                .min(0, 'Please use a value from 0 to 100')
                .max(100, 'Please use a value from 0 to 100')
                .integer('Please provide a whole number value from 0 to 100');
            }
            return schema.notRequired();
          },
          otherwise: (schema) => schema.notRequired(),
        }),
      globalAllowancePercentage: yup.number().when([], {
        is: () => companyConfiguration?.modulesConfig?.modules?.RESERVE === 'ENABLED',
        then: (schema) =>
          schema
            .integer('Global limit must be an integer')
            .transform((value, originalValue) => (originalValue === '' ? undefined : value))
            .required('Global allowance is always required')
            .test(
              'valid-global-allowance-percentage',
              'Please select a valid option',
              function validateGlobalAllowancePercentage(value) {
                // If it's 200 (Custom %), it's always valid
                if (value === 200) {
                  return true;
                }
                // For preset values, check if it's within the allowed range
                return value >= 0 && value <= 100;
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      customGlobalAllowancePercentage: yup
        .number()
        .integer('Custom Global limit must be an integer')
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .when('globalAllowancePercentage', {
          is: 200, // When globalAllowancePercentage is 200 (Custom %)
          then: (schema) => {
            // Only require custom global allowance percentage if RESERVE is enabled
            if (companyConfiguration?.modulesConfig?.modules?.RESERVE === 'ENABLED') {
              return schema
                .required('Custom Global limit is required when choosing Custom %')
                .min(0, 'Please use a value from 0 to 100')
                .max(100, 'Please use a value from 0 to 100')
                .integer('Please provide a whole number value');
            }
            return schema.notRequired();
          },
          otherwise: (schema) => schema.notRequired(),
        }),
    })
    .test(
      'sub-limit-not-higher-than-global',
      'On-demand pay sub-limit cannot be higher than Global limit',
      function validateSubLimitNotHigherThanGlobal(values) {
        const {
          odpAllowancePercentage = 0,
          customOdpAllowancePercentage = 0,
          globalAllowancePercentage = 0,
          customGlobalAllowancePercentage = 0,
        } = values;

        // Calculate the actual global limit value
        const actualGlobalLimitValue =
          globalAllowancePercentage === 200
            ? customGlobalAllowancePercentage ?? 0
            : globalAllowancePercentage ?? 0;

        // Calculate the actual sub-limit value
        const actualSubLimitValue =
          odpAllowancePercentage === 200
            ? customOdpAllowancePercentage ?? 0
            : odpAllowancePercentage ?? 0;

        // Check if sub-limit is higher than global limit
        if (actualSubLimitValue > actualGlobalLimitValue) {
          // If it's a custom value, attach error to customOdpAllowancePercentage field
          // Otherwise, attach to odpAllowancePercentage dropdown
          const errorPath =
            odpAllowancePercentage === 200
              ? 'customOdpAllowancePercentage'
              : 'odpAllowancePercentage';
          return this.createError({
            path: errorPath,
            message: 'On-demand pay sub-limit cannot be higher than Global limit',
          });
        }
        return true;
      }
    );

export default createPayCalendarProfileFormSchema;
